import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Shield, Phone, Home, ChevronDown, Menu, X, 
  Calculator, Search, DollarSign, MapPin,
  BookOpen, HelpCircle, FileText, Users,
  Clock, Award, Heart
} from 'lucide-react';
import { Helmet } from 'react-helmet';

export default function EnhancedNavigationSystem() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  // Navigation structure
  const navigation = {
    main: [
      { name: 'Home', path: '/', icon: Home },
      { name: 'About', path: '/about', icon: Users },
      { name: 'Services', path: '/services', icon: Award },
      { 
        name: 'Tools', 
        path: '#',
        icon: Calculator,
        dropdown: [
          { name: 'Payment Calculator', path: '/payment-plans-page', icon: Calculator, description: 'Calculate monthly payment options' },
          { name: 'Inmate Search', path: '/inmate-search-widget', icon: Search, description: 'Search Sacramento County inmates' },
          { name: 'Bail Estimator', path: '/payment-plans-page#estimator', icon: DollarSign, description: 'Estimate bail bond costs' },
        ]
      },
      { name: 'Locations', path: '/locations', icon: MapPin },
      { 
        name: 'Resources', 
        path: '#',
        icon: BookOpen,
        dropdown: [
          { name: 'Blog', path: '/blog', icon: BookOpen, description: 'Latest bail bond news and guides' },
          { name: 'Bail Guide & FAQ', path: '/5-step-bail-guide-with-faq', icon: HelpCircle, description: '5-step process with answers' },
          { name: 'Legal Information', path: '/legal-disclaimer', icon: FileText, description: 'Terms and legal disclaimers' },
          { name: 'CSAP Program', path: '/client-success-accountability-program-csap', icon: Heart, description: 'Client success support program' },
        ]
      },
      { name: 'Contact', path: '/contact-us', icon: Phone },
    ],
    emergency: {
      phone: '(916) 256-3468',
      text: '24/7 Emergency Bail'
    }
  };

  const toggleDropdown = (name) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  return (
    <>
      <Helmet>
        <title>Enhanced Navigation System | Sailors Bail Bonds</title>
        <meta
          name="description"
          content="Demonstration of the enhanced navigation system with standardized dropdown menus and improved information architecture for Sailors Bail Bonds."
        />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-teal-700">
        {/* Enhanced Navigation Header */}
        <nav className="bg-gradient-to-r from-blue-800 via-blue-700 to-teal-600 shadow-2xl sticky top-0 z-50 border-b-4 border-teal-400">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-20">
              {/* Logo */}
              <Link to="/" className="flex items-center space-x-3 group">
                <div className="relative w-12 h-12">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-teal-400 rounded-full group-hover:animate-pulse opacity-20"></div>
                  <svg
                    className="relative w-full h-full text-white drop-shadow-lg"
                    viewBox="0 0 100 100"
                    fill="currentColor"
                  >
                    <path d="M50 10 L50 70 M35 55 Q50 70 65 55 M50 10 Q40 10 40 20 Q40 25 50 25 Q60 25 60 20 Q60 10 50 10 M30 45 L70 45" />
                  </svg>
                </div>
                <span className="text-2xl font-bold text-white">Sailors Bail Bonds</span>
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center space-x-1">
                {/* Client Access Button */}
                <a
                  href="https://agents.simplybail.com/m/sailorsbailbonds43/office?code"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-gradient-to-r from-yellow-400 to-amber-500 text-black px-4 py-2 rounded-lg font-bold text-sm hover:from-yellow-300 hover:to-amber-400 transition-all transform hover:scale-105 shadow-lg border-2 border-yellow-300 hover:border-yellow-200 flex items-center mr-4"
                >
                  <Shield className="w-4 h-4 mr-2 group-hover:animate-pulse" />
                  Client Access
                </a>

                {/* Main Navigation Items */}
                {navigation?.main?.map((item) => (
                  <div
                    key={item?.name}
                    className="relative"
                    onMouseEnter={() => item?.dropdown && toggleDropdown(item?.name)}
                    onMouseLeave={() => item?.dropdown && toggleDropdown(null)}
                  >
                    {item?.dropdown ? (
                      <>
                        <button
                          className={`flex items-center px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                            isActivePath(item?.path)
                              ? 'bg-white/20 text-white' :'text-blue-100 hover:bg-white/10 hover:text-white'
                          }`}
                          aria-expanded={activeDropdown === item?.name}
                          aria-haspopup="true"
                        >
                          <item.icon className="w-4 h-4 mr-2" />
                          {item?.name}
                          <ChevronDown className="w-4 h-4 ml-1" />
                        </button>

                        {/* Dropdown Menu */}
                        {activeDropdown === item?.name && (
                          <div className="absolute left-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border-2 border-blue-200 overflow-hidden">
                            {item?.dropdown?.map((dropdownItem) => (
                              <Link
                                key={dropdownItem?.name}
                                to={dropdownItem?.path}
                                className="flex items-start px-6 py-4 hover:bg-blue-50 transition-colors group border-b border-gray-100 last:border-b-0"
                              >
                                <dropdownItem.icon className="w-5 h-5 text-blue-600 mt-0.5 mr-3 group-hover:scale-110 transition-transform" />
                                <div>
                                  <div className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                                    {dropdownItem?.name}
                                  </div>
                                  <div className="text-xs text-gray-600 mt-1">
                                    {dropdownItem?.description}
                                  </div>
                                </div>
                              </Link>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <Link
                        to={item?.path}
                        className={`flex items-center px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                          isActivePath(item?.path)
                            ? 'bg-white/20 text-white' :'text-blue-100 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        <item.icon className="w-4 h-4 mr-2" />
                        {item?.name}
                      </Link>
                    )}
                  </div>
                ))}

                {/* Emergency CTA */}
                <a
                  href={`tel:${navigation?.emergency?.phone?.replace(/[^\d]/g, '')}`}
                  className="ml-4 bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-3 rounded-full font-bold text-sm hover:from-red-500 hover:to-red-600 transition-all shadow-lg flex items-center border-2 border-red-400 hover:border-red-300"
                >
                  <Clock className="w-4 h-4 mr-2 animate-pulse" />
                  {navigation?.emergency?.text}: {navigation?.emergency?.phone}
                </a>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden bg-white/10 text-white p-2 rounded-lg hover:bg-white/20 transition-colors"
                aria-label="Toggle mobile menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden bg-blue-900/95 backdrop-blur-md border-t border-blue-700">
              <div className="max-w-7xl mx-auto px-4 py-6 space-y-2">
                {/* Client Access */}
                <a
                  href="https://agents.simplybail.com/m/sailorsbailbonds43/office?code"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center bg-gradient-to-r from-yellow-400 to-amber-500 text-black px-4 py-3 rounded-lg font-bold hover:from-yellow-300 hover:to-amber-400 transition-all"
                >
                  <Shield className="w-5 h-5 mr-3" />
                  Client Access
                </a>

                {/* Mobile Navigation Items */}
                {navigation?.main?.map((item) => (
                  <div key={item?.name}>
                    {item?.dropdown ? (
                      <>
                        <button
                          onClick={() => toggleDropdown(item?.name)}
                          className="w-full flex items-center justify-between text-white px-4 py-3 rounded-lg hover:bg-white/10 transition-colors font-semibold"
                        >
                          <span className="flex items-center">
                            <item.icon className="w-5 h-5 mr-3" />
                            {item?.name}
                          </span>
                          <ChevronDown
                            className={`w-5 h-5 transition-transform ${
                              activeDropdown === item?.name ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        {activeDropdown === item?.name && (
                          <div className="ml-8 mt-2 space-y-2">
                            {item?.dropdown?.map((dropdownItem) => (
                              <Link
                                key={dropdownItem?.name}
                                to={dropdownItem?.path}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="flex items-start text-blue-100 px-4 py-2 rounded-lg hover:bg-white/10 transition-colors"
                              >
                                <dropdownItem.icon className="w-4 h-4 mt-0.5 mr-3" />
                                <div>
                                  <div className="font-semibold">{dropdownItem?.name}</div>
                                  <div className="text-xs text-blue-200 mt-1">
                                    {dropdownItem?.description}
                                  </div>
                                </div>
                              </Link>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <Link
                        to={item?.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex items-center px-4 py-3 rounded-lg font-semibold transition-colors ${
                          isActivePath(item?.path)
                            ? 'bg-white/20 text-white' :'text-blue-100 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        <item.icon className="w-5 h-5 mr-3" />
                        {item?.name}
                      </Link>
                    )}
                  </div>
                ))}

                {/* Mobile Emergency CTA */}
                <a
                  href={`tel:${navigation?.emergency?.phone?.replace(/[^\d]/g, '')}`}
                  className="flex items-center bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-4 rounded-lg font-bold hover:from-red-500 hover:to-red-600 transition-all border-2 border-red-400"
                >
                  <Clock className="w-5 h-5 mr-3 animate-pulse" />
                  {navigation?.emergency?.text}: {navigation?.emergency?.phone}
                </a>
              </div>
            </div>
          )}
        </nav>

        {/* Page Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Enhanced Navigation System
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto">
              Standardized dropdown menus and improved information architecture for seamless user experience
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-8 border border-white/20">
              <Calculator className="w-12 h-12 text-teal-300 mb-4" />
              <h3 className="text-2xl font-bold text-white mb-3">Tools Dropdown</h3>
              <p className="text-blue-100">
                Quick access to Payment Calculator, Inmate Search, and Bail Estimator with visual icons and descriptions
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-xl p-8 border border-white/20">
              <BookOpen className="w-12 h-12 text-teal-300 mb-4" />
              <h3 className="text-2xl font-bold text-white mb-3">Resources Dropdown</h3>
              <p className="text-blue-100">
                Organized access to Blog, FAQ, Legal Information, and CSAP Program with helpful descriptions
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-xl p-8 border border-white/20">
              <Phone className="w-12 h-12 text-teal-300 mb-4" />
              <h3 className="text-2xl font-bold text-white mb-3">24/7 Emergency</h3>
              <p className="text-blue-100">
                Persistent emergency contact with click-to-call functionality always visible across all devices
              </p>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="bg-gradient-to-r from-blue-600/30 to-teal-600/30 backdrop-blur-md rounded-2xl p-12 border-2 border-white/30">
            <h2 className="text-3xl font-bold text-white text-center mb-8">Key Benefits</h2>
            <div className="grid md:grid-cols-2 gap-6 text-blue-100">
              <div className="flex items-start">
                <div className="bg-teal-400 rounded-full p-2 mr-4 flex-shrink-0">
                  <Users className="w-5 h-5 text-blue-900" />
                </div>
                <div>
                  <h4 className="font-bold text-white mb-2">Reduced Cognitive Load</h4>
                  <p>Organized dropdown menus help users find information quickly during crisis situations</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-teal-400 rounded-full p-2 mr-4 flex-shrink-0">
                  <Award className="w-5 h-5 text-blue-900" />
                </div>
                <div>
                  <h4 className="font-bold text-white mb-2">Consistent Experience</h4>
                  <p>Standardized navigation across all pages ensures users always know where they are</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-teal-400 rounded-full p-2 mr-4 flex-shrink-0">
                  <Shield className="w-5 h-5 text-blue-900" />
                </div>
                <div>
                  <h4 className="font-bold text-white mb-2">ADA Compliant</h4>
                  <p>Keyboard navigation, ARIA labels, and screen reader compatibility throughout</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-teal-400 rounded-full p-2 mr-4 flex-shrink-0">
                  <MapPin className="w-5 h-5 text-blue-900" />
                </div>
                <div>
                  <h4 className="font-bold text-white mb-2">Mobile Optimized</h4>
                  <p>Full-screen overlay menu with touch-friendly interactions for mobile users</p>
                </div>
              </div>
            </div>
          </div>

          {/* Implementation Guide */}
          <div className="mt-16 bg-white/10 backdrop-blur-md rounded-2xl p-12 border border-white/20">
            <h2 className="text-3xl font-bold text-white mb-8">Implementation Guidelines</h2>
            <div className="space-y-6 text-blue-100">
              <div>
                <h3 className="text-xl font-bold text-white mb-3">1. Navigation Structure</h3>
                <p>The header includes: Logo + Client Access + Main Nav Items + Tools Dropdown + Resources Dropdown + Contact + Emergency CTA</p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-3">2. Dropdown Behavior</h3>
                <p>Desktop: Hover to open, click to navigate. Mobile: Touch to expand/collapse with clear visual feedback</p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-3">3. Visual Hierarchy</h3>
                <p>Emergency CTA uses red gradient, Client Access uses yellow gradient, active page highlighted with white overlay</p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-3">4. Brand Consistency</h3>
                <p>3D anchor logo, gradient blue theme, nautical colors maintained throughout all interactions</p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Experience It Yourself</h2>
            <p className="text-xl text-blue-100 mb-8">
              Navigate through the site to see the enhanced navigation system in action
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="inline-flex items-center justify-center bg-white text-blue-600 hover:bg-blue-50 font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <Home className="w-6 h-6 mr-3" />
                Go to Homepage
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center justify-center bg-teal-600 hover:bg-teal-700 text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 border-2 border-white"
              >
                <Users className="w-6 h-6 mr-3" />
                Visit About Page
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}